import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { PurchaseOrderService } from "services/purchase-order";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const createRow = () => ({
  itemName: "",
  purchaseAddress: "",
  unit: "",
  quantity: "",
  unitPrice: "",
});

const splitDate = (text = "") => {
  const [day = "", month = "", year = ""] = String(text).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const composeDate = (parts) => {
  if (!parts.day || !parts.month || !parts.year) return "";
  return `${String(parts.day).padStart(2, "0")}/${String(parts.month).padStart(2, "0")}/${parts.year}`;
};

const validateDate = (parts, label) => {
  const date = composeDate(parts);
  if (!date) throw new Error(`${label} chưa hợp lệ`);
  const [d, m, y] = date.split("/").map(Number);
  const maxDay = new Date(y, m, 0).getDate();
  if (m < 1 || m > 12 || y < 1900 || y > 9999 || d < 1 || d > maxDay) {
    throw new Error(`${label} không hợp lệ`);
  }
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
};

const parseQty = (value) => {
  const normalized = String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");
  if (!normalized) return 0;
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Number(numeric.toFixed(3));
};

const formatQtyInput = (value) => String(value || "").replace(/,/g, ".").replace(/[^\d.]/g, "");

const formatQtyOutput = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "";
  if (Math.abs(number - Math.trunc(number)) < 0.000001) return `${Math.trunc(number)}`;
  return number.toFixed(3).replace(/\.0+$/, "").replace(/(\.\d*?)0+$/, "$1");
};

const roundMoney = (value) => {
  const parsed = parseVndIntegerInput(value);
  return Number.isFinite(parsed) ? Math.round(parsed) : 0;
};

const fmtMoney = (value) => new Intl.NumberFormat("vi-VN").format(Math.round(Number(value) || 0));

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const today = new Date();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });

  const [header, setHeader] = useState({
    unit: "",
    department: "",
    bookNumber: "",
    documentNumber: "",
    debitAccount: "",
    creditAccount: "",
    buyerName: "",
    buyerDepartment: "",
  });

  const [rows, setRows] = useState([createRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await PurchaseOrderService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          bookNumber: data.bookNumber || "",
          documentNumber: data.documentNumber || "",
          debitAccount: data.debitAccount || "",
          creditAccount: data.creditAccount || "",
          buyerName: data.buyerName || "",
          buyerDepartment: data.buyerDepartment || "",
        });

        setDocumentDateParts(splitDate(data.documentDate || ""));

        const mappedRows = (data.rows || []).map((row) => ({
          itemName: row.itemName || "",
          purchaseAddress: row.purchaseAddress || "",
          unit: row.unit || "",
          quantity: formatQtyOutput(row.quantity),
          unitPrice: formatVndIntegerInput(row.unitPrice || ""),
        }));

        setRows(mappedRows.length ? mappedRows : [createRow()]);
      } catch (error) {
        toast.error("Không thể tải dữ liệu bảng kê mua hàng");
      }
    })();
  }, [id]);

  const derivedRows = useMemo(() =>
    rows.map((row) => {
      const quantity = parseQty(row.quantity);
      const unitPrice = roundMoney(row.unitPrice);
      const amount = Math.round(quantity * unitPrice);
      return {
        ...row,
        quantity,
        unitPrice,
        amount,
      };
    }),
  [rows]);

  const totalAmount = useMemo(
    () => derivedRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [derivedRows]
  );

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setDocumentDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setDocumentDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const setRowField = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      let nextValue = value;
      if (field === "unitPrice") {
        nextValue = formatVndIntegerInput(value);
      } else if (field === "quantity") {
        nextValue = formatQtyInput(value);
      }
      next[index] = { ...next[index], [field]: nextValue };
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, createRow()]);
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const documentDate = validateDate(documentDateParts, "Ngày lập bảng kê");
    const bookNumber = String(header.bookNumber || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const debitAccount = String(header.debitAccount || "").trim();
    const creditAccount = String(header.creditAccount || "").trim();
    const buyerName = String(header.buyerName || "").trim();
    const buyerDepartment = String(header.buyerDepartment || "").trim();

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!documentNumber) throw new Error("Số bảng kê không được để trống");
    if (!buyerName) throw new Error("Họ và tên người mua không được để trống");
    if (!buyerDepartment) throw new Error("Bộ phận (phòng, ban) không được để trống");

    const normalizedRows = derivedRows
      .map((row, index) => ({
        serialNumber: String(index + 1),
        itemName: String(row.itemName || "").trim(),
        purchaseAddress: String(row.purchaseAddress || "").trim(),
        unit: String(row.unit || "").trim(),
        quantity: row.quantity,
        unitPrice: row.unitPrice,
        amount: row.amount,
      }))
      .filter((row) => {
        const hasQty = row.quantity > 0 || row.unitPrice > 0 || row.amount > 0;
        return row.itemName || row.purchaseAddress || row.unit || hasQty;
      });

    if (!normalizedRows.length) {
      throw new Error("Bảng kê mua hàng phải có ít nhất 1 dòng dữ liệu");
    }

    normalizedRows.forEach((row, index) => {
      if (!row.itemName) throw new Error(`Dòng ${index + 1}: Tên, quy cách, phẩm chất không được để trống`);
    });

    return {
      unit,
      department,
      documentDate,
      bookNumber,
      documentNumber,
      debitAccount,
      creditAccount,
      buyerName,
      buyerDepartment,
      rows: normalizedRows,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await PurchaseOrderService.create(payload);
        toast.success("Tạo bảng kê mua hàng thành công");
      } else {
        await PurchaseOrderService.update(id, payload);
        toast.success("Cập nhật bảng kê mua hàng thành công");
      }
      navigation("/templates/purchase-order");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      headerInfo: {
        ...header,
        documentDate: composeDate(documentDateParts),
      },
      rows: derivedRows.map((row, index) => ({ ...row, serialNumber: String(index + 1) })),
      totalAmount,
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const s = {
    container: {
      fontFamily: '"Times New Roman", Times, serif',
      color: "#000",
      padding: "20px",
      backgroundColor: "#fff",
      fontSize: "16px",
    },
    inputDotted: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      minWidth: 20,
    },
    inputDate: {
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontSize: "16px",
      fontFamily: "inherit",
      background: "transparent",
      color: "#000",
      width: 36,
      textAlign: "center",
    },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 980 },
    th: { border: "1px solid #000", padding: 4, textAlign: "center", fontWeight: "bold", verticalAlign: "middle" },
    td: { border: "1px solid #000", padding: 0, height: 34 },
    inputLeft: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "left",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputCenter: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "center",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
    inputRight: {
      width: "100%",
      height: "100%",
      border: "none",
      outline: "none",
      textAlign: "right",
      fontSize: 14,
      fontFamily: "inherit",
      background: "transparent",
      padding: "0 6px",
      display: "block",
      color: "#000",
    },
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={2} style={s.container}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                  <div style={{ lineHeight: 1.5 }}>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...s.inputDotted, width: 220 }}
                        value={header.department}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </div>

                  <div style={{ textAlign: "center", fontSize: 15, lineHeight: 1.35 }}>
                    <div>
                      <b>Mẫu số: 06 - VT</b>
                    </div>
                    <div>
                      <i>(Kèm theo Thông tư số 99/2025/TT-BTC</i>
                    </div>
                    <div>
                      <i>ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</i>
                    </div>
                  </div>
                </div>

                <div style={{ textAlign: "center", marginTop: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 28, textTransform: "uppercase" }}>
                    BẢNG KÊ MUA HÀNG
                  </div>
                  <div style={{ marginTop: 8, fontStyle: "italic" }}>
                    Ngày{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.day}
                      onChange={(event) => setDateField("day", event.target.value)}
                      onBlur={() => padDateField("day")}
                    />{" "}
                    tháng{" "}
                    <input
                      disabled={containView}
                      style={s.inputDate}
                      value={documentDateParts.month}
                      onChange={(event) => setDateField("month", event.target.value)}
                      onBlur={() => padDateField("month")}
                    />{" "}
                    năm{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDate, width: 56 }}
                      value={documentDateParts.year}
                      onChange={(event) => setDateField("year", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ textAlign: "right", marginTop: 8, lineHeight: 1.6 }}>
                  <div>
                    Quyển số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.bookNumber}
                      onChange={(event) => setHeaderField("bookNumber", event.target.value)}
                    />
                  </div>
                  <div>
                    Số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.documentNumber}
                      onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                    />
                  </div>
                  <div>
                    Nợ:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.debitAccount}
                      onChange={(event) => setHeaderField("debitAccount", event.target.value)}
                    />
                  </div>
                  <div>
                    Có:{" "}
                    <input
                      disabled={containView}
                      style={{ ...s.inputDotted, width: 120, textAlign: "center" }}
                      value={header.creditAccount}
                      onChange={(event) => setHeaderField("creditAccount", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 6 }}>
                  - Họ và tên người mua:{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 240 }}
                    value={header.buyerName}
                    onChange={(event) => setHeaderField("buyerName", event.target.value)}
                  />
                </div>
                <div style={{ marginTop: 6 }}>
                  - Bộ phận (phòng, ban):{" "}
                  <input
                    disabled={containView}
                    style={{ ...s.inputDotted, width: 240 }}
                    value={header.buyerDepartment}
                    onChange={(event) => setHeaderField("buyerDepartment", event.target.value)}
                  />
                </div>

                <div style={{ overflowX: "auto", border: "1px solid #ccc", marginTop: 10 }}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={{ ...s.th, width: 60 }}>STT</th>
                        <th style={{ ...s.th, width: 320 }}>Tên, quy cách, phẩm chất hàng hóa (vật tư, công cụ...)</th>
                        <th style={{ ...s.th, width: 220 }}>Địa chỉ mua hàng</th>
                        <th style={{ ...s.th, width: 120 }}>Đơn vị tính</th>
                        <th style={{ ...s.th, width: 110 }}>Số lượng</th>
                        <th style={{ ...s.th, width: 140 }}>Đơn giá</th>
                        <th style={{ ...s.th, width: 160 }}>Thành tiền</th>
                        {!containView ? <th style={{ ...s.th, width: 40 }}>#</th> : null}
                      </tr>
                      <tr>
                        <th style={s.th}>A</th>
                        <th style={s.th}>B</th>
                        <th style={s.th}>C</th>
                        <th style={s.th}>D</th>
                        <th style={s.th}>1</th>
                        <th style={s.th}>2</th>
                        <th style={s.th}>3</th>
                        {!containView ? <th style={s.th}>#</th> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => {
                        const calcRow = derivedRows[index];
                        return (
                          <tr key={`purchase-order-row-${index}`}>
                            <td style={{ ...s.td, textAlign: "center", fontWeight: 600 }}>{index + 1}</td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputLeft}
                                value={row.itemName}
                                onChange={(event) => setRowField(index, "itemName", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputLeft}
                                value={row.purchaseAddress}
                                onChange={(event) => setRowField(index, "purchaseAddress", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputCenter}
                                value={row.unit}
                                onChange={(event) => setRowField(index, "unit", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.quantity}
                                onChange={(event) => setRowField(index, "quantity", event.target.value)}
                              />
                            </td>
                            <td style={s.td}>
                              <input
                                disabled={containView}
                                style={s.inputRight}
                                value={row.unitPrice}
                                onChange={(event) => setRowField(index, "unitPrice", event.target.value)}
                              />
                            </td>
                            <td style={{ ...s.td, textAlign: "right", padding: "0 6px" }}>{fmtMoney(calcRow.amount)}</td>
                            {!containView ? (
                              <td style={{ ...s.td, textAlign: "center" }}>
                                <button
                                  type="button"
                                  onClick={() => removeRow(index)}
                                  style={{ border: "none", background: "transparent", color: "#d32f2f", fontWeight: 700, cursor: "pointer" }}
                                >
                                  x
                                </button>
                              </td>
                            ) : null}
                          </tr>
                        );
                      })}

                      <tr>
                        <td colSpan={6} style={{ ...s.td, textAlign: "center", fontWeight: 700 }}>Cộng</td>
                        <td style={{ ...s.td, textAlign: "right", padding: "0 6px", fontWeight: 700 }}>{fmtMoney(totalAmount)}</td>
                        {!containView ? <td style={s.td} /> : null}
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
                  <SoftButton variant="outlined" color="dark" onClick={() => navigation(-1)}>
                    Trở về
                  </SoftButton>

                  <div style={{ display: "flex", gap: 12 }}>
                    {!containView ? (
                      <SoftButton variant="outlined" color="dark" onClick={addRow}>
                        THÊM DÒNG
                      </SoftButton>
                    ) : null}

                    {containView ? (
                      <SoftButton variant="gradient" color="success" onClick={handlePrint}>
                        In Bảng
                      </SoftButton>
                    ) : null}

                    {!containView ? (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {id ? "Lưu" : "Tạo"}
                      </SoftButton>
                    ) : null}
                  </div>
                </div>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
